import { PortalTelemetryEvent } from '../../app/telemetry/portalTelemetryEvents';
import { PortalTelemetryScreen } from '../../app/telemetry/portalTelemetryScreens';
import { validationIssues } from '../../app/telemetry/validationTelemetry';
import { getInitialEnrolmentStep, hasStepDetailsChanges, mapToRegistrationDetailsDto, mapToPersonalDetailsDto, mapToUpdateEducationCommand, mapToWorkDetailsDto } from './enrolmentFormLogic';
import { Box, Button, Grid, Step, StepLabel, Stepper } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import PersonalDetails from './PersonalDetails';
import { registrationValidationSchema } from '../../app/models/pcsEnrolment/registrationValidationSchema';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAppDispatch, useAppSelector } from '../../app/store/configureStore';
import {
  API_REQUEST_FULLFILLED, MESSAGE_DETAILS_SAVED, MESSAGE_SAVE_AND_CONTINUE,
  MESSAGE_NEXT, MESSAGE_BACK_TO_PREVIOUS_STEP, MESSAGE_PROCESSING_DETAILS
} from '../../app/utils/constant';
import { updatePersonalDetailsStateAsync, updateVerifyPersonalDetailsAsync } from '../../app/slices/personSlice';
import { ApiRequestStatus } from '../../app/enums/apiRequestStatus';
import LoadingComponent from '../../app/layout/LoadingComponent';
import { toast } from 'react-toastify';
import { useMsal } from '@azure/msal-react';
import { UserClaims } from '../../app/enums/userClaims';
import EducationalDetails from './EducationalDetails';
import RegistrationDetails from './RegistrationDetails';
import registrationFormModel from '../../app/models/pcsEnrolment/registrationFormModel';
import Header from './Header';
import { useReferenceData } from '../../app/customHooks/useReferenceData';
import { useGetPersonalDetails } from '../../app/customHooks/useGetPersonalDetails';
import { useGetRegistrationDetails } from '../../app/customHooks/useGetRegistrationDetails';
import { PcsEnrolmentStepEnum } from '../../app/models/pcsEnrolment/pcsEnrolmentStepEnum';
import { saveWorkpostAsync } from '../../app/slices/workpostSlice';
import { saveRegistrationDetailsAsync, updateEducationAsync } from '../../app/slices/pcsSlice';
import WorkDetails from './workDetails/WorkDetails';
import Payment from './Payment';
import { SpaName } from '../../app/enums/spaName';
import { useCanEnrol } from '../../app/customHooks/useCanEnrol';
import Unauthorized from '../../app/errors/Unauthorized';
import { PCS_ALREADY_ENROLLED } from '../membershipRenewal/common/messages';
import { useCanReEnrol } from '../../app/customHooks/useCanReEnrol';
import { useGetPcsEnrolmentYears } from '../../app/customHooks/useGetPcsEnrolmentYears';
import { useGetPcsCurrentYear } from '../../app/customHooks/useGetPcsCurrentYear';
import { pcsEnrolmentStatusEnum } from '../../app/enums/pcsEnrolmentStatusEnum';
import PcsDetailsMessages from './PcsDetailsMessages';
import { startPcsJourney, trackPcsSaveAsync, trackPcsEvent, trackPcsOnce, trackPcsReturn } from '../../app/telemetry/portalTelemetry';

interface Prop {
  reenrol: boolean;
}

export default function Enrolment({ reenrol }: Prop) {
  const queryParams = new URLSearchParams(window.location.search);
  const expectedStep = queryParams.get("expectedStep");
  const initialStep = getInitialEnrolmentStep(expectedStep);
  const enteredAt = useRef(performance.now());
  const entryTracked = useRef(false);

  useEffect(() => {
    startPcsJourney(reenrol ? 'reenrolment' : 'enrolment');
    if (expectedStep === '4' && !entryTracked.current) trackPcsReturn('returned_to_payment');
    entryTracked.current = true;
  }, [reenrol, expectedStep]);

  const steps = [PcsEnrolmentStepEnum.RegistrationDetails, PcsEnrolmentStepEnum.PersonalDetails,
  PcsEnrolmentStepEnum.EducationalDetails, PcsEnrolmentStepEnum.WorkDetails, PcsEnrolmentStepEnum.Payment];

  function getStepContent(step: number) {
    switch (step) {
      case 0:
        return <RegistrationDetails reenrol={reenrol} />
      case 1:
        return <PersonalDetails reenrol={reenrol} />
      case 2:
        return <EducationalDetails reenrol={reenrol} />
      case 3:
        return <WorkDetails nextStepClick={() => {
          trackPcsEvent(PortalTelemetryEvent.WorkDetailsConfirmed, { step: steps[activeStep] });
          trackPcsEvent(PortalTelemetryEvent.Navigation, { step: steps[activeStep], direction: 'next' });
          setActiveStep(prevActiveStep => prevActiveStep + 1);
        }} handleSave={handleNext} spaName={SpaName.PCS_ENROLMENT} />
      case 4:
        return <Payment reenrol={reenrol} />
      default:
        throw new Error('Unknown step');
    }
  }

  const [activeStep, setActiveStep] = useState(initialStep);
  const currentValidationSchema = registrationValidationSchema[activeStep];
  const methods = useForm({
    mode: 'all',
    resolver: yupResolver(currentValidationSchema)
  });
  const { trigger, formState: { dirtyFields } } = methods;
  const hasDetailsChanges = hasStepDetailsChanges(activeStep, dirtyFields);
  const form = methods.watch();

  const { accounts } = useMsal();

  useEffect(() => {
    if (accounts.length > 0 && accounts[0]?.idTokenClaims) {
      setPcsFeesDue(accounts[0].idTokenClaims[UserClaims.EXTENSION_PORTALCRM_PCSFEESDUE] as boolean);
    }
  }, [accounts]);

  const [pcsFeesDue, setPcsFeesDue] = useState<boolean | undefined>();

  const dispatch = useAppDispatch();

  const { pcsEnrolmentTypes, staticDataStateStatus } = useAppSelector(state => state.ReferenceData);
  const { canEnrol, canReEnrol, currentYear, registerStateStatus, saveRegistrationDetailsStatus, currentYearStatus,
    updateEducationalDetailsStatus, canEnrolStatus, canReEnrolStatus } = useAppSelector(state => state.pcs);
  const { personalDetails, personalDetailsStatus, updatePersonalDetailsStatus, updateVerifyPersonalDetailsStatus }
    = useAppSelector(state => state.personalDetail);
  const { workpostStatus, workpostListStatus } = useAppSelector(state => state.workpost);
  const { enrolmentYears, enrolmentYearsStatus, canPayMembershipStatus, finalisePaymentCheckStatus }
    = useAppSelector(state => state.pcsEnrolment);

  useReferenceData();
  useGetRegistrationDetails();
  useGetPersonalDetails();
  useCanEnrol(reenrol);
  useCanReEnrol(reenrol);
  useGetPcsCurrentYear();
  useGetPcsEnrolmentYears();

  const detailsReady = [staticDataStateStatus, registerStateStatus, personalDetailsStatus,
    enrolmentYearsStatus, currentYearStatus, reenrol ? canReEnrolStatus : canEnrolStatus]
    .every(status => status === ApiRequestStatus.Fulfilled);
  const isLoading = [staticDataStateStatus, registerStateStatus, personalDetailsStatus,
    saveRegistrationDetailsStatus, updatePersonalDetailsStatus, updateEducationalDetailsStatus,
    workpostStatus, canEnrolStatus, canReEnrolStatus, enrolmentYearsStatus, currentYearStatus,
    updateVerifyPersonalDetailsStatus].some(status => status === ApiRequestStatus.Pending);
  const blocked = (reenrol && canReEnrol === false) || (!reenrol && canEnrol === false);
  const applicationPending = pcsEnrolmentTypes?.find(y => y.id === enrolmentYears?.find(x => x.year === currentYear)?.pcsEnrolmentStatusId)?.code === pcsEnrolmentStatusEnum.Pending;
  const stepReady = detailsReady && (activeStep !== 3 || workpostListStatus === ApiRequestStatus.Fulfilled)
    && (activeStep !== 4 || (canPayMembershipStatus === ApiRequestStatus.Fulfilled
      && (!reenrol || finalisePaymentCheckStatus === ApiRequestStatus.Fulfilled)));

  useEffect(() => { enteredAt.current = performance.now(); }, [activeStep]);

  useEffect(() => {
    if (!detailsReady || isLoading || (reenrol && pcsFeesDue === undefined)) return;
    if (blocked) trackPcsOnce(PortalTelemetryEvent.EligibilityBlocked, 'blocked');
    else if (applicationPending) trackPcsOnce(PortalTelemetryEvent.ApplicationPending, 'pending');
    else if (stepReady) {
      trackPcsOnce(PortalTelemetryEvent.StepViewed, `step:${activeStep}`, { step: steps[activeStep] },
        { durationMs: performance.now() - enteredAt.current });
    }
  }, [detailsReady, isLoading, reenrol, pcsFeesDue, blocked, applicationPending, stepReady, activeStep]);

  const isFulfilled = (response: any) =>
    response.meta.requestStatus.toLowerCase() === API_REQUEST_FULLFILLED;

  const handleNext = async () => {
    const isStepValid = await trigger();
    if (!isStepValid) {
      trackPcsEvent(PortalTelemetryEvent.ValidationBlocked, { step: steps[activeStep], validationErrors: validationIssues(PortalTelemetryScreen.Enrolment, methods.getFieldState) });
      return;
    }
    trackPcsEvent(PortalTelemetryEvent.Navigation, { step: steps[activeStep], direction: 'next' });

    if (!hasDetailsChanges) {
      if (activeStep === 1) {
        const response = await trackPcsSaveAsync('personal_details_verify', () => dispatch(updateVerifyPersonalDetailsAsync()), steps[activeStep]);

        if (isFulfilled(response)) {
          toast.success(MESSAGE_DETAILS_SAVED);
        }
      }

      setActiveStep(prev => prev + 1);
      return;
    }

    const isEligible = reenrol ? canReEnrol : canEnrol;
    if (isEligible !== true) return;

    switch (activeStep) {
      case 0: {
        const registrationDetails = mapToRegistrationDetailsDto(form);

        const saveResponse = await trackPcsSaveAsync('registration_save', () => dispatch(saveRegistrationDetailsAsync(registrationDetails)), steps[activeStep]);

        if (!isFulfilled(saveResponse)) return;

        toast.success(MESSAGE_DETAILS_SAVED);

        methods.reset({ ...form, [registrationFormModel.registrationDetailsFormField.UserConfirmation.name]: [] });
        setActiveStep(prev => prev + 1);

        break;
      }

      case 1: {
        const personalDetailsDto = mapToPersonalDetailsDto(form, personalDetails);

        const saveResponse = await trackPcsSaveAsync('personal_details_save', () => dispatch(updatePersonalDetailsStateAsync(personalDetailsDto)), steps[activeStep]);

        if (!isFulfilled(saveResponse)) return;

        toast.success(MESSAGE_DETAILS_SAVED);

        methods.reset({ ...form });
        setActiveStep(prev => prev + 1);

        break;
      }

      case 2: {
        const saveResponse = await trackPcsSaveAsync('education_save', () => dispatch(updateEducationAsync(mapToUpdateEducationCommand(form))), steps[activeStep]);

        if (!isFulfilled(saveResponse)) return;

        toast.success(MESSAGE_DETAILS_SAVED);

        methods.reset({ ...form });
        setActiveStep(prev => prev + 1);

        break;
      }

      case 3: {
        const workpostDto = mapToWorkDetailsDto(form);

        const saveResponse = await trackPcsSaveAsync('work_details_save', () => dispatch(saveWorkpostAsync(workpostDto)), steps[activeStep]);

        if (!isFulfilled(saveResponse)) return;

        toast.success(MESSAGE_DETAILS_SAVED);
        setActiveStep(prev => prev + 1);
        break;
      }

      default:
        console.warn("Not a valid step");
        break;
    }
  };

  async function _handleBack() {
    trackPcsEvent(PortalTelemetryEvent.Navigation, { step: steps[activeStep], direction: 'back' });
    // Discard unsaved edits before the previous step loads its saved values.
    // Otherwise its initialization can be blocked by this step's dirty state.
    methods.reset();
    setActiveStep(activeStep - 1);
  }

  const [width, setWidth] = useState<number>(window.innerWidth);

  const handleWindowSizeChange = () => {
    setWidth(window.innerWidth);
  }

  useEffect(() => {
    window.addEventListener('resize', handleWindowSizeChange);
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange);
    }
  }, []);

  const isMobile = width <= 768;

  if (isLoading) {
    return <LoadingComponent message={MESSAGE_PROCESSING_DETAILS} />
  }

  if (blocked) {
    return <Unauthorized message={PCS_ALREADY_ENROLLED} />
  }

  if (applicationPending) {
    return <PcsDetailsMessages reenrol={reenrol}
      pcsEnrolmentStatus={pcsEnrolmentTypes?.find(y => y.id === enrolmentYears?.find(x => x.year === currentYear)?.pcsEnrolmentStatusId)?.code} />
  }

  return (
    <FormProvider {...methods}>
      <Header reenrol={reenrol} />

      <br></br>

      <Stepper activeStep={activeStep} orientation={isMobile === true ? "vertical" : "horizontal"}>
        {steps.map((label) => {
          const stepProps = {};
          const labelProps = {};
          return (
            <Step key={label} {...stepProps}>
              <StepLabel {...labelProps}>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>

      <br></br>

      {activeStep !== 0 && (
        <Button variant="contained" onClick={_handleBack}>
          {MESSAGE_BACK_TO_PREVIOUS_STEP}
        </Button>
      )}

      {activeStep < steps.length &&
        <>
          {getStepContent(activeStep)}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Grid container columnSpacing={12}>

              <Grid size={{ xs: 12 }}></Grid>

              <Grid size={{ xs: 11 }} sx={{ display: 'flex', justifyContent: 'end' }}>
                {activeStep <= steps.length - 3 && <Button variant="contained" color="primary" onClick={handleNext}>
                  {hasDetailsChanges ? MESSAGE_SAVE_AND_CONTINUE : MESSAGE_NEXT}
                </Button>}
              </Grid>

              <Grid size={{ xs: 1 }}></Grid>
            </Grid>
          </Box>
        </>}
    </FormProvider >
  );
};
